import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlackForm } from 'shared/types';
import {
  ALERT_RULES,
  DESTINATION_OPTIONS,
  INTEGRATION_OPTIONS,
  SLACK,
} from '@elementary/common-ui/constants/queryKeys';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import * as api from '../../services/settingsApi';
import useSnackbar from '../useSnackbar';
import useSendTracking from './useSendTracking';

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useSlackIntegration = ({ envId, onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const slackQuery = useQuery([SLACK, envId], () => api.fetchSlack(envId!), {
    enabled: !!envId,
  });

  const slackIntegrationMutation = useMutation(
    () => api.setSlackIntegration(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SLACK]);
        queryClient.invalidateQueries([DESTINATION_OPTIONS]);
        queryClient.invalidateQueries([INTEGRATION_OPTIONS]);
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.CONNECT_SLACK_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_SLACK_INTEGRATION_FAILURE,
        }),
    },
  );

  const slackMutation = useMutation(
    (data: SlackForm) => {
      const reqData = {
        channelName: data?.channelName.trim(),
      };

      return slackQuery.data?.defaultDestination
        ? api.updateSlack(reqData, envId!)
        : api.setSlack(reqData, envId!);
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([SLACK]);
        queryClient.invalidateQueries([ALERT_RULES]);
        openSnackbar({
          message: 'Successfully connected to your Slack',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.SET_SLACK_DEFAULT_DESTINATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.SET_SLACK_DEFAULT_DESTINATION_FAILURE,
        }),
    },
  );

  const slackChannelsMutation = useMutation(
    () => api.fetchSlackChannels(envId!),
    {
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.LIST_SLACK_CHANNELS_FAILURE,
        }),
    },
  );

  return {
    isSlackConnected: !!slackQuery.data?.defaultDestination,
    slackQuery,
    slackIntegrationMutation,
    slackMutation,
    slackChannelsMutation,
  };
};

export default useSlackIntegration;
